import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { PiSquareSplitVertical, PiColumns, PiTable } from 'react-icons/pi';
import { Route as CommunicationsRoute } from 'src/routes/communications';
import { communicationsSearchSchema } from 'src/modules/shared/schemas/communications-search.schema';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import type { DropdownItem } from 'src/modules/shared/components/molecules/dropdowns/dropdown';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { getMenuItemContentClasses } from 'src/modules/shared/styles/dropdown.styles';
import { getControlTextSize } from 'src/modules/shared/components/atoms/text/text.styles';
import type { CommunicationsView } from 'src/modules/shared/types/communications-view.type';
import { useResolvedCommunicationsView } from 'src/modules/communications/hooks/use-resolved-communications-view';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

/**
 * ViewSwitcher component that allows switching between different conversation views.
 * Displayed as a dropdown with the current view label and icon.
 */
const ViewSwitcher: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { view } = useSearch({ from: CommunicationsRoute.id });
  const { resolvedView } = useResolvedCommunicationsView(view);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleViewChange = useCallback(
    (newView: CommunicationsView) => {
      void navigate({
        to: CommunicationsRoute.to,
        search: (prev) => {
          const parsedSearch = communicationsSearchSchema.parse(prev);
          return {
            ...parsedSearch,
            view: newView,
          };
        },
      });
    },
    [navigate]
  );

  const views: {
    value: CommunicationsView;
    label: string;
    icon: React.ReactNode;
  }[] = useMemo(
    () => [
      {
        value: 'cards',
        label: t('communications.viewSwitcher.cards'),
        icon: <PiSquareSplitVertical className="h-4 w-4" />,
      },
      {
        value: 'pipeline',
        label: t('communications.viewSwitcher.pipeline'),
        icon: <PiColumns className="h-4 w-4" />,
      },
      {
        value: 'table',
        label: t('communications.viewSwitcher.table'),
        icon: <PiTable className="h-4 w-4" />,
      },
    ],
    [t]
  );

  const selectedView = useMemo(
    () => views.find((v) => v.value === resolvedView),
    [resolvedView, views]
  );

  const dropdownItems: DropdownItem[] = useMemo(
    () =>
      views.map((viewOption) => ({
        content: viewOption.label,
        icon: viewOption.icon,
        onClick: () => handleViewChange(viewOption.value),
        selected: viewOption.value === resolvedView,
      })),
    [handleViewChange, resolvedView, views]
  );

  return (
    <Tooltip
      enabled={!isDropdownOpen}
      content={t('communications.viewSwitcher.tooltip', {
        view: selectedView?.label ?? '',
      })}
      placement="bottom"
      size="md"
      className="w-[16rem]"
    >
      <MenuDropdown
        items={dropdownItems}
        icon={selectedView?.icon}
        label={
          <Text
            size={getControlTextSize('md')}
            weight="regular"
            color="secondary"
            className={`${getMenuItemContentClasses()} whitespace-nowrap truncate hidden xl:inline`}
          >
            {selectedView?.label}
          </Text>
        }
        variant="secondary"
        appearance="outline"
        size="md"
        placement="bottom-start"
        onOpenChange={setIsDropdownOpen}
      />
    </Tooltip>
  );
};

export default ViewSwitcher;
